import type { User } from '@/types/User'
import type {
    AuthResponse,
    LoginRequest,
    RegisterRequest,
    VerifyOtpRequest,
    OtpSetupResponse,
    ForgotPasswordResponse
} from '@/types/ApiResponse'
import { API_ENDPOINTS } from '@/config/apiConstants'
import { BaseApiClient } from './BaseApiClient'
import { tokenService } from './TokenService'

class AuthService extends BaseApiClient {
    /**
     * User login
     */
    async login(credentials: LoginRequest): Promise<AuthResponse> {
        try {
            const response = await this.post<AuthResponse>(
                API_ENDPOINTS.AUTH.LOGIN,
                credentials
            )

            // Only mark as authenticated after MFA is cleared
            if (!response.mfaRequired) {
                tokenService.setAuthenticated(true, credentials.rememberMe)
            }

            return response
        } catch (error) {
            tokenService.setAuthenticated(false)
            throw error
        }
    }

    /**
     * User registration
     */
    async register(userData: RegisterRequest): Promise<AuthResponse> {
        try {
            const response = await this.post<AuthResponse>(
                API_ENDPOINTS.AUTH.REGISTER,
                userData
            )

            if (response.mfaRequired) {
                return response
            }

            tokenService.setAuthenticated(true)
            return response
        } catch (error) {
            tokenService.setAuthenticated(false)
            throw error
        }
    }

    /**
     * Verify OTP code
     */
    async verifyOtp(request: VerifyOtpRequest): Promise<AuthResponse> {
        try {
            // Ensure persistence state is carried from login if not explicitly provided
            const isPersistent = request.rememberMe ?? tokenService.isPersistent()

            const response = await this.post<AuthResponse>(
                API_ENDPOINTS.AUTH.VERIFY_OTP,
                { ...request, rememberMe: isPersistent }
            )

            tokenService.setAuthenticated(true, isPersistent)
            return response
        } catch (error) {
            tokenService.setAuthenticated(false)
            throw error
        }
    }

    /**
     * Logout
     */
    async logout(): Promise<void> {
        try {
            await this.post(API_ENDPOINTS.AUTH.LOGOUT)
        } catch (error) {
            // Silently fail or handle error
        } finally {
            tokenService.clearAuthState()
            // Force reload to clear any memory states
            window.location.href = '/login'
        }
    }

    getCurrentUser(): User | null {
        return tokenService.getUser()
    }

    isAuthenticated(): boolean {
        return tokenService.isAuthenticated()
    }

    /**
     * Fetch current user info from /me endpoint
     * This is required because HttpOnly cookies cannot be read by JS
     */
    async getMe(): Promise<User> {
        try {
            const user = await this.get<User>(API_ENDPOINTS.ACCOUNT.ME)
            tokenService.setUser(user)
            return user
        } catch (error: any) {
            // Only clear auth state on 401 — network/500 errors should not log the user out
            const status = error?.response?.status ?? error?.status
            if (status === 401) {
                tokenService.clearAuthState()
            }
            throw error
        }
    }

    /**
     * Initiate 2FA Setup
     */
    async initiate2FA(type: 'GOOGLE_AUTH' | 'SMS' | 'EMAIL' | 'PASSKEY' = 'GOOGLE_AUTH'): Promise<OtpSetupResponse> {
        return this.post<OtpSetupResponse>(API_ENDPOINTS.AUTH.INITIATE_2FA, { type })
    }

    /**
     * Verify 2FA Setup to enable it
     */
    async verify2FASetup(code: string): Promise<void> {
        await this.post(API_ENDPOINTS.AUTH.VERIFY_2FA_SETUP, { code })
        await this.getMe() // Refresh user state to update isOtpEnabled
    }

    /**
     * Disable 2FA
     */
    async disable2FA(): Promise<void> {
        await this.post(API_ENDPOINTS.AUTH.DISABLE_2FA)
        await this.getMe()
    }

    /**
     * Send password reset OTP
     */
    async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
        return this.post<ForgotPasswordResponse>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email })
    }

    /**
     * Verify password reset OTP
     */
    async verifyResetOtp(email: string, code: string): Promise<void> {
        await this.post(API_ENDPOINTS.AUTH.VERIFY_RESET_OTP, { email, code })
    }

    /**
     * Reset password with OTP
     */
    async resetPassword(email: string, code: string, password: string): Promise<void> {
        await this.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, { email, code, newPassword: password })
    }

    /**
     * Complete user profile on first login or one-time identity recovery
     */
    async completeProfile(data: {
        firstname: string
        lastname: string
        email?: string
        dateOfBirth?: string
        phoneNumber?: string
        password?: string
        provinceId?: number
        districtId?: number
        streamCategoryId?: string
    }): Promise<User> {
        const user = await this.put<User>(API_ENDPOINTS.ACCOUNT.COMPLETE_PROFILE, data)
        tokenService.setUser(user)
        return user
    }

    /**
     * Update user profile settings
     */
    async updateProfile(data: { firstname?: string; lastname?: string; dateOfBirth?: string; phoneNumber?: string; password?: string; provinceId?: number; districtId?: number; streamCategoryId?: string }): Promise<User> {
        const user = await this.put<User>(API_ENDPOINTS.ACCOUNT.UPDATE_PROFILE, data)
        // Update stored user data
        const currentUser = tokenService.getUser()
        if (currentUser) {
            tokenService.setUser({ ...currentUser, ...user })
        }
        return user
    }

    /**
     * Initiate phone verification
     */
    async initiatePhoneVerification(): Promise<void> {
        await this.post(API_ENDPOINTS.ACCOUNT.VERIFY_PHONE_INITIATE)
    }

    /**
     * Confirm phone verification
     */
    async confirmPhoneVerification(code: string): Promise<void> {
        await this.post(API_ENDPOINTS.ACCOUNT.VERIFY_PHONE_CONFIRM, { code })
        await this.getMe() // Refresh user data to update phoneVerified status
    }

    /**
     * Initiate email verification
     */
    async initiateEmailVerification(): Promise<void> {
        await this.post(API_ENDPOINTS.ACCOUNT.VERIFY_EMAIL_INITIATE)
    }

    /**
     * Confirm email verification
     */
    async confirmEmailVerification(code: string): Promise<void> {
        await this.post(API_ENDPOINTS.ACCOUNT.VERIFY_EMAIL_CONFIRM, { code })
        await this.getMe() // Refresh user data to update emailVerified status
    }
}

export const authService = new AuthService()
